import { useEffect, useRef } from 'react';
import type L from 'leaflet';

/**
 * Moves the map to `center` the first time it is known, unless the user has already
 * moved the map.
 *
 * MapContainer reads `center` only when it mounts, and the recall map usually mounts
 * before the tenant (and so its coordinates) has loaded. Without this, a refresh that
 * loses that race leaves the map on the default center. It acts once: a later change,
 * such as a tenant switch, never re-centers a map the user is looking at.
 */
export function useCenterOnceWhenKnown(
  map: L.Map | null,
  center: [number, number] | undefined,
  zoom: number
): void {
  const doneRef = useRef(false);
  const userMovedRef = useRef(false);
  const lat = center?.[0];
  const lng = center?.[1];

  // Drags, zooms and fly-to searches all count: any of them means "don't move my map".
  useEffect(() => {
    if (!map) return undefined;
    const markMoved = () => {
      userMovedRef.current = true;
    };
    map.on('dragstart zoomstart', markMoved);
    return () => {
      map.off('dragstart zoomstart', markMoved);
    };
  }, [map]);

  useEffect(() => {
    if (!map || lat === undefined || lng === undefined || doneRef.current) return;
    doneRef.current = true;
    if (userMovedRef.current) return;
    const current = map.getCenter();
    if (Math.abs(current.lat - lat) < 1e-6 && Math.abs(current.lng - lng) < 1e-6) return;
    // Not animated, so the resulting moveend reports the new bounds straight away.
    map.setView([lat, lng], zoom, { animate: false });
  }, [map, lat, lng, zoom]);
}
